// Dependencies
import React from 'react';

// Hooks
import { useRouter } from 'next/router';
import useResponsive from 'src/hooks/useResponsive';

// External Components
import NextLink from 'next/link';

// Material UI Components
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import IconButton from '@mui/material/IconButton';

// Components
import Logo from 'src/components/Logo';
import NavigationList from './NavigationList';
import NavAccount from './NavAccount';

// Internal Components
import LanguagePopover from './LanguagePopover';
import NotificationsPopover from './NotificationsPopover';
import ModePopover from './ModePopover';
import { IconButtonAnimate } from 'src/components/animate';
import Iconify from 'src/components/Iconify';

// Utils
import getFromObject from 'src/utils/getFromObject';

// Types
type Props = {
  sideBar: boolean;
  onSideBar: (open: boolean) => void;
};

/**
 * Check if the device is iOS
 */
const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

/**
 * Navbar component for the dashboard layout
 */
export default function NavBar({ sideBar, onSideBar }: Props) {
  // ===================================
  // Hooks
  const isDesktop = useResponsive('up', 'lg');
  const router = useRouter();

  const render = (
    <Box width={34 * 8} height="100%" position="relative">
      <Box position="absolute" top={0} left={0} right={0} bottom={0}>
        <Stack
          height="100%"
          direction="column"
          sx={{
            py: 3,
          }}
          width="100%"
          spacing={2}
          alignItems="center"
        >
          <Stack direction="row" width="100%" px={2} justifyContent="center" alignItems="center">
            <Logo hrefOverride="/dashboard" />
          </Stack>
          <Stack
            direction="row"
            width="100%"
            gap={1}
            px={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <ModePopover sideBarOpen={sideBar} />
            <LanguagePopover />
            <NotificationsPopover />
            {router.pathname.includes('/dashboard/manager') &&
              router.query?.page === 'preferences' && (
                <IconButton
                  disabled={true}
                  color={'default'}
                  sx={{
                    width: 40,
                    height: 40,
                  }}
                >
                  <Iconify icon="pajamas:preferences" width={20} height={20} />
                </IconButton>
              )}
            {!(
              router.pathname.includes('/dashboard/manager') && router.query?.page === 'preferences'
            ) && (
              <NextLink
                href={{
                  pathname: '/dashboard/manager/preferences',
                  query: router.pathname.includes('/dashboard/manager')
                    ? router.query
                    : getFromObject(router)
                    ? {
                        from: getFromObject(router),
                      }
                    : undefined,
                }}
                passHref
              >
                <IconButtonAnimate
                  color={'default'}
                  sx={{
                    width: 40,
                    height: 40,
                  }}
                >
                  <Iconify icon="pajamas:preferences" width={20} height={20} />
                </IconButtonAnimate>
              </NextLink>
            )}
          </Stack>
          <NavigationList onClick={() => onSideBar(false)} />
          <Box mt={1.5} />
          <NavAccount onClick={() => onSideBar(false)} />
        </Stack>
      </Box>
    </Box>
  );

  if (isDesktop) {
    return render;
  }
  return (
    <SwipeableDrawer
      open={sideBar}
      onClose={() => onSideBar(false)}
      onOpen={() => onSideBar(true)}
      PaperProps={{
        sx: {
          position: 'relative',
          width: 34 * 8,
          bgcolor: (theme) => theme.palette.background.default,
          transition: (theme) =>
            theme.transitions.create('width', {
              duration: theme.transitions.duration.standard,
            }),
        },
      }}
      disableBackdropTransition={!iOS}
      disableDiscovery={iOS}
      keepMounted
    >
      {render}
    </SwipeableDrawer>
  );
}
